/**
 * DriverEmergencyContacts — self-serve emergency-contact management for the
 * Driver App. Contacts are notified (by a dispatcher, or automatically on
 * escalation) if the driver raises an SOS. Safety/SOS module only.
 */
import React, { useState, useEffect, useCallback } from "react";
import { Users, Plus, Trash2, Loader2, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export const DriverEmergencyContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", phone: "", email: "", relationship: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/driver/emergency-contacts"); setContacts(data.contacts || []); }
    catch { /* best-effort */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim()) return toast.error("Contact name is required");
    if (!form.phone.trim() && !form.email.trim()) return toast.error("Add a phone or email");
    setSaving(true);
    try {
      await api.post("/driver/emergency-contacts", form);
      setForm({ name: "", phone: "", email: "", relationship: "" });
      toast.success("Emergency contact added.");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/driver/emergency-contacts/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="driver-emergency-contacts">
      <div className="flex items-center gap-1.5 text-sm font-bold mb-1"><Users className="w-4 h-4" /> Emergency contacts</div>
      <p className="text-xs text-muted-foreground mb-2">People we'll alert if you raise an SOS. They are never contacted otherwise.</p>

      {loading ? (
        <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
      ) : (
        <div className="space-y-1.5 mb-2">
          {contacts.length === 0 && <div className="text-xs text-muted-foreground border border-dashed border-border rounded-lg p-2">No emergency contacts yet.</div>}
          {contacts.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-2 border border-border rounded-lg px-2.5 py-1.5 text-xs" data-testid={`driver-contact-${c.id}`}>
              <span><b>{c.name}</b>{c.relationship ? ` · ${c.relationship}` : ""}{c.phone ? ` · ${c.phone}` : ""}{c.email ? ` · ${c.email}` : ""}</span>
              <button onClick={() => remove(c.id)} data-testid={`driver-contact-remove-${c.id}`} className="text-[#DC2626] p-1 hover:bg-secondary rounded"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Name" data-testid="driver-contact-name"
          className="border border-border rounded-lg px-2.5 py-1.5 text-sm" />
        <input value={form.relationship} onChange={(e) => setForm((f) => ({ ...f, relationship: e.target.value }))} placeholder="Relationship" data-testid="driver-contact-relationship"
          className="border border-border rounded-lg px-2.5 py-1.5 text-sm" />
        <input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="Phone" data-testid="driver-contact-phone"
          className="border border-border rounded-lg px-2.5 py-1.5 text-sm" />
        <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" data-testid="driver-contact-email"
          className="border border-border rounded-lg px-2.5 py-1.5 text-sm" />
      </div>
      <button onClick={add} disabled={saving} data-testid="driver-contact-add"
        className="mt-2 text-xs font-semibold bg-[#0A0A0A] text-white rounded-lg px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Add contact
      </button>

      <div className="mt-2 text-[10px] text-muted-foreground flex items-start gap-1">
        <ShieldCheck className="w-3 h-3 shrink-0 mt-0.5" /> Used only for SOS safety alerts.
      </div>
    </div>
  );
};

export default DriverEmergencyContacts;
